import { ArchBaseService } from "@/common/api.service";

/**
 * Skill authoring, versioning and publishing (ENG-2243).
 *
 * A third AI service rather than more methods on `aiAdminService`, for the same reason
 * that one is separate from `aiWorkspaceService`: the permissions differ. Authoring sits
 * behind `ai.skill.manage`, publishing behind `ai.skill.publish`, and the settings screen
 * behind `ai.config.manage` — three grants that are deliberately not the same people. The
 * backend splits the controllers on exactly this line; keeping the client split the same
 * way means a screen can never reach for a write it has no business making.
 *
 * The split worth honouring hardest is publish. Authoring is private and reversible — a
 * draft affects nobody. Publishing takes effect on everyone's next message with no review
 * step in between, so `publishVersion` is the one call here that changes what users see,
 * and the interface treats it accordingly.
 *
 * `ArchBaseService.init()` runs before every request because every service in
 * `common/api.service.js` writes the same shared `axios.defaults.baseURL` — whichever
 * `init()` ran most recently owns it globally.
 */
export const AiSkillAdminService = {
  /**
   * Every Skill, including drafts and archived ones.
   *
   * Deliberately wider than `GET ai/skills`, which serves the user-facing picker and
   * returns published, non-archived Skills only. A draft is invisible to users and must
   * not be invisible to the person writing it, and an archived Skill has to stay
   * reachable because restoring one is a `PATCH`, not an undelete.
   *
   * @returns {Promise} [{ id, slug, name, description, status, isProtected,
   *   publishedVersion, versionCount, createdAt, updatedAt }]
   */
  getSkills() {
    ArchBaseService.init();
    return ArchBaseService.get("ai/admin/skills");
  },

  /**
   * One Skill with every version and its body.
   *
   * The list endpoint carries no bodies; this is the only call that does, which is why
   * the modal fetches it on open rather than working from the row it was given.
   *
   * @param {Number} id
   * @returns {Promise} the skill view, plus `versions` newest first
   */
  getSkill(id) {
    ArchBaseService.init();
    return ArchBaseService.get(`ai/admin/skills/${id}`);
  },

  /**
   * Create the Skill and the first draft of its text together.
   *
   * One request on purpose: a Skill with no version can neither be published nor
   * selected, so creating the two separately only ever produces a row in that state.
   * `status` and `isProtected` cannot be set here — a new Skill is always a draft.
   *
   * @param {Object} skill - { slug, name, description, body }
   * @returns {Promise} the created skill detail view
   */
  createSkill(skill) {
    ArchBaseService.init();
    return ArchBaseService.post("ai/admin/skills", skill);
  },

  /**
   * Rename, re-describe, re-slug, or change the Skill's own status.
   *
   * Only what changed, never the whole form — the same discipline the config PATCH
   * takes, and for the same reason: a form that round-trips every field is how one
   * administrator's edit silently reverts another's.
   *
   * `status` here is the Skill's, which answers "is this offered in the picker?" — not
   * the version's, which answers "is this the live text?". Restoring an archived Skill
   * is `{ status: 'published' }` and is a separate decision from publishing new text.
   *
   * @param {Number} id
   * @param {Object} changes - only the fields being changed
   * @returns {Promise} the updated skill detail view
   */
  updateSkill(id, changes) {
    ArchBaseService.init();
    return ArchBaseService.patch(`ai/admin/skills/${id}`, changes);
  },

  /**
   * Start a new draft of the instructions.
   *
   * This is also how a rollback is expressed: there is no "re-publish an old version",
   * because a published version is immutable and an archived one stays archived. Going
   * back means creating a new draft carrying the old body and publishing that, so the
   * history stays a record of what actually served messages.
   *
   * @param {Number} id
   * @param {String} body
   * @returns {Promise} the created version view
   */
  createVersion(id, body) {
    ArchBaseService.init();
    return ArchBaseService.post(`ai/admin/skills/${id}/versions`, { body });
  },

  /**
   * Edit a draft's body.
   *
   * Only a draft. A published version's body is immutable and the backend refuses the
   * edit outright (`E1018-026`), which is why the interface omits the control rather
   * than offering one that fails — the immutability is the feature, not an obstacle.
   *
   * @param {Number} id
   * @param {Number} versionId
   * @param {String} body
   * @returns {Promise} the updated version view
   */
  updateVersion(id, versionId, body) {
    ArchBaseService.init();
    return ArchBaseService.patch(`ai/admin/skills/${id}/versions/${versionId}`, { body });
  },

  /**
   * Make this draft the live text. Behind `ai.skill.publish`, not `ai.skill.manage`.
   *
   * Takes effect on everyone's next message — there is no review step between this call
   * and a user reading the result. Publishing v2 archives v1 in the same transaction,
   * and the database's unique index is the thing actually holding "exactly one published
   * version", so a second one racing in comes back as `E1018-025` rather than being
   * quietly accepted.
   *
   * Publishing the first version also promotes a `draft` Skill to `published`, so it
   * reaches the picker without a second step. An `archived` Skill stays archived.
   *
   * @param {Number} id
   * @param {Number} versionId
   * @returns {Promise} the updated skill detail view
   */
  publishVersion(id, versionId) {
    ArchBaseService.init();
    return ArchBaseService.post(`ai/admin/skills/${id}/versions/${versionId}/publish`);
  },
};

export default AiSkillAdminService;
